import { Svg, G, Element as SvgElement } from '@svgdotjs/svg.js';
import { Point, Region } from 'core/geometry';

export enum CropHandlePosition
{
  TopLeft,
  TopRight,
  BottomLeft,
  BottomRight,
}

interface HandleCornerFeatures
{
  extraCssClass: string;
  rotation: number;

  corner(region: Region): Point;
  opposite(region: Region): Point;
  offset(sideLength: number): Point;
}

const CORNER_FEATURES: {[position: number]: HandleCornerFeatures} = {
  [CropHandlePosition.TopLeft]: {
    extraCssClass: 'corner-top-left',
    rotation: 0,
    corner: (region) => region.topLeft,
    opposite: (region) => region.bottomRight,
    offset: () => new Point(0, 0),
  },

  [CropHandlePosition.TopRight]: {
    extraCssClass: 'corner-top-right',
    rotation: 90,
    corner: (region) => new Point(region.bottomRight.x, region.y),
    opposite: (region) => new Point(region.x, region.bottomRight.y),
    offset: (sideLength) => new Point(-sideLength, 0),
  },

  [CropHandlePosition.BottomLeft]: {
    extraCssClass: 'corner-bottom-left',
    rotation: 270,
    corner: (region) => new Point(region.x, region.bottomRight.y),
    opposite: (region) => new Point(region.bottomRight.x, region.y),
    offset: (sideLength) => new Point(0, -sideLength),
  },

  [CropHandlePosition.BottomRight]: {
    extraCssClass: 'corner-bottom-right',
    rotation: 180,
    corner: (region) => region.bottomRight,
    opposite: (region) => region.topLeft,
    offset: (sideLength) => new Point(-sideLength, -sideLength),
  },
};

export class CropHandleCorner
{
  private static readonly BASE_SIZE: number = 24;
  private readonly cornerFeatures: HandleCornerFeatures;

  private _shape: G;
  private _sideLength: number = 0;

  constructor(
      private readonly svgDoc: Svg,
      private readonly container: SvgElement,
      public readonly position: CropHandlePosition)
  {
    this.cornerFeatures = CORNER_FEATURES[position];

    this._shape = this.svgDoc.group();
    this.fillShape();
  }

  public get sideLength(): number
  {
    return this._sideLength;
  }

  public get shape(): SvgElement
  {
    return this._shape;
  }

  private fillShape(): void
  {
    const unit = this.sideLength;
    const half = unit / 2;

    this._shape
      .rect(unit, unit)
      .addClass('image-cropper-corner-area')
      .addClass(this.cornerFeatures.extraCssClass);

    this._shape
      .line(0, 0, unit, 0)
      .addClass('image-cropper-corner-line')
      .rotate(this.cornerFeatures.rotation, half, half);

    this._shape
      .line(0, 0, 0, unit)
      .addClass('image-cropper-corner-line')
      .rotate(this.cornerFeatures.rotation, half, half);
  }

  private updateSideLength(
      widthConstraint: number, heightConstraint: number): number
  {
    const newSideLength =
        Math.floor(
            Math.min(
                CropHandleCorner.BASE_SIZE,
                Math.max(
                    Math.min(widthConstraint, heightConstraint) / 3,
                    4)));

    if (this.sideLength !== newSideLength) {
      this._sideLength = newSideLength;

      this._shape.clear();
      this.fillShape();
    }

    return this.sideLength;
  }

  public update(): void
  {
    const containerBounds = Region.fromSimilar(this.container.bbox());

    this._sideLength =
        this.updateSideLength(containerBounds.width, containerBounds.height);

    const position =
        this.cornerFeatures
            .corner(containerBounds)
            .add(this.cornerFeatures.offset(this.sideLength));

    this._shape.move(position.x, position.y);
  }
}

export interface OppositeCornersInfo
{
  same: Point;
  opposite: Point;
}

export function getOppositeCorners(
    region: Region,
    position: CropHandlePosition): OppositeCornersInfo
{
  const features = CORNER_FEATURES[position];

  return {
    same: features.corner(region),
    opposite: features.opposite(region),
  };
}
